
import { Component, Emit, Mixins, Watch } from "vue-property-decorator";
import TreePicker from '@/components/form/TreePicker.vue';
import SystemViewHealthFilter from "./SystemViewHealthFilter.vue";
import AreaTreePath from "@/views/AreaSetup/AreaTreePath.vue";
import GroupRow from "@/types/sv-data/groups/GroupRow";
import api from "@/services/api.service";
import SystemViewTreeNode from '@/types/sv-data/system-view/SystemViewTreeNode';
import SystemViewHealthStatusCount from '@/types/sv-data/system-view/SystemViewHealthStatusCount';
import ObjectTypeId from '@/types/sv-data/enums/ObjectTypeIds';
import { Getter, namespace } from "vuex-class";
import SystemViewAdvancedFilters from "@/components/system-view/advanced-filters/SystemViewAdvancedFilters.vue";
import { SystemViewPaginatedSearchQueryParams, UserPermissions, VueBootstrapField } from "@/store/types";
import OpsLinks from '../OpsLinks.vue';
import PaginatedSearch from "@/mixins/PaginatedSearch";
import { SystemViewResponse } from "@/types/sv-data/system-view/SystemViewPaginatedResponse";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import SystemViewChildHealthResponse from "../../types/sv-data/system-view/SystemViewChildHealthResponse";
import SystemViewChildHealthRequest from "@/types/sv-data/system-view/SystemViewChildHealthRequest";

const SystemHealth = namespace("systemHealth");

enum ViewLevels {
	Appliances = 0,
	Servers = 1,
	Syncs = 2,
	Devices = 3
}

@Component({
	components: {
		TreePicker,
		SystemViewHealthFilter,
		AreaTreePath,
		SystemViewAdvancedFilters,
		"ops-links": OpsLinks,
		VuePerfectScrollbar: VuePerfectScrollbar
	}
})
export default class SystemViewTree extends Mixins(PaginatedSearch) {

	@Getter getUserGroupId: number;
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@SystemHealth.Getter('getHealthStatus') private healthStatusCount: SystemViewHealthStatusCount;
	@SystemHealth.Getter('getStatusFilter') private statusFilter: string;
	@SystemHealth.Mutation setHealthStatus: (status: SystemViewHealthStatusCount) => void;
	@SystemHealth.Mutation setStatusFilter: (filter: string) => void;

	private ViewLevelsEnum: typeof ViewLevels = ViewLevels;
	private viewLevelSelected: ViewLevels = ViewLevels.Servers;

	private viewPermissions: string [] = ['CanViewSystemView'];
	private selectedPage: number = 1;
	private resultsPerPage: string = "25";
	private systemViewData: SystemViewResponse[] = []

	private selectedAppliance: SystemViewResponse = null;
	private selectedServer: SystemViewResponse = null;
	private selectedSync: SystemViewResponse = null;

	private selectedArea: GroupRow[] = [];
	private rootArea: GroupRow[] = [];
	private titleFilter: string = null;
	private isLoading: boolean = false;
	private isViewLoading: boolean = false;
	private isLoadingAreas: boolean = false;

	private selectedGroupId: number = null;
	private advancedFilterId: number = null;
	private previousAdvancedFilterId: number = null;

	private totalRecords: number = 0;
	private recordsPage: number = 1;
	private recordsPageSize: number = 25;

	private healthStatus = "All";

	private pageHealthStats: SystemViewChildHealthResponse[] = null;

	private requestParams: SystemViewPaginatedSearchQueryParams = {
		page: this.recordsPage,
		pageSize: this.recordsPageSize,
		filter: null,
		excludeHealthyItems: false,
		excludeUnhealthyItems: false,
		excludeUntestedItems: false,
		advancedFilterId: null,
		applianceId: null,
		serverId: null,
		groupSyncId: null,
	};

	public loadMoreLabel: string = "Load more...";

	private get systemViewDataTableFields(): VueBootstrapField[] {
		let fields = [
			{ key: "icon", label: "Icon", sortable: false, tdClass: "text-center" } as VueBootstrapField,
			{ key: "title", label: "Title", sortable: false } as VueBootstrapField,
			{ key: "actions", label: "Actions", sortable: false , tdClass: "text-right"} as VueBootstrapField,
			{ key: "health", label: "Health", sortable: false, tdClass: ["text-center", "w-25", "pr-3"] } as VueBootstrapField,
		] as VueBootstrapField[];

		return fields;
	}

	private async mounted(): Promise<void> {
		this.onStatusFilterChange();
		await this.setupAreas();
	}

	private async setupAreas(): Promise<void> {
		// Setup the Area Select
		try {
			this.isLoadingAreas = true;
			const rootGroupsPagedResponse = await api.getRootGroups(this.viewPermissions, this.selectedPage, parseInt(this.resultsPerPage));

			if (!!rootGroupsPagedResponse &&
				!!rootGroupsPagedResponse.data &&
				rootGroupsPagedResponse.data.length === 1) {
				this.selectedArea = rootGroupsPagedResponse.data;
				this.selectedGroupId = this.selectedArea[0].groupID;
				this.rootArea = rootGroupsPagedResponse.data;
			} else {
				this.selectedGroupId = this.getUserGroupId;
			}
		}
		catch (ex) {
			console.error("Unexpected error occurred while filtering areas on System View: " + ex);
		}

		this.isLoadingAreas = false;
	}

	private get isAdvancedFilteringEnabled(): boolean {
		return this.getFeature(["SystemView", "AdvancedStatusFilters"])
			&& (this.permissions.canViewAdvancedStatusFilters || this.permissions.canEditAdvancedStatusFilters);
	}

	@Watch("selectedGroupId")
	private async onSelectedGroupIdChanged(): Promise<void> {
		this.recordsPage = 1;
		await this.getViewData();
	}

	@Watch("healthStatusCount")
	private async onHealthStatusCountChanged(): Promise<void> {
	}

	@Watch("titleFilter")
	private async onTitleFilterChanged(): Promise<void> {
		this.recordsPage = 1;
		this.requestParams.filter = this.titleFilter;
		await this.getViewData();
	}

	private async onAdvancedFilterInput(): Promise<void> {
		if(!this.isAdvancedFilteringEnabled) {
			return;
		}
		this.requestParams.advancedFilterId = this.advancedFilterId;
		if(this.previousAdvancedFilterId !== this.advancedFilterId || this.advancedFilterId !== null) {
			this.previousAdvancedFilterId = this.advancedFilterId;
			await this.getViewData();
		}
	}

	@Watch("requestParams")
	private async updateTable(): Promise<void> {
		await this.getViewData();
	}

	private async getViewData(): Promise<void> {
		this.requestParams.page = this.recordsPage;
		this.requestParams.applianceId = this.selectedAppliance ? this.selectedAppliance.objectId : null;
		this.requestParams.serverId = this.selectedServer ? this.selectedServer.objectId : null;
		this.requestParams.groupSyncId = this.selectedSync ? this.selectedSync.objectId : null;

		if (this.selectedGroupId) {
			this.isLoading = true;
			try {
				switch(this.viewLevelSelected) {
					case this.ViewLevelsEnum.Appliances:{
						const result = await api.getSystemViewAppliances(this.selectedGroupId, this.generateNewPaginatedSearchRequest(this.requestParams));
						this.systemViewData = result.data
						this.totalRecords = result.totalRecords;
						break;
					}

					case this.ViewLevelsEnum.Syncs: {
						const result = await api.getSystemViewSyncs(this.selectedGroupId, this.generateNewPaginatedSearchRequest(this.requestParams))
						this.systemViewData = result.data
						this.totalRecords = result.totalRecords;
						break;
					}

					default:
					case this.ViewLevelsEnum.Servers: {
						const result = await api.getSystemViewServers(this.selectedGroupId, this.generateNewPaginatedSearchRequest(this.requestParams));
						this.systemViewData = result.data
						this.totalRecords = result.totalRecords;
						break;
					}

					case this.ViewLevelsEnum.Devices: {
						const result = await api.getSystemViewDevices(this.selectedGroupId, this.generateNewPaginatedSearchRequest(this.requestParams))
						this.systemViewData = result.data
						this.totalRecords = result.totalRecords;
						break;
					}
				}
			}
			catch (ex) {
				console.error("Unexpected error loading SystemView Item tree: " + ex);
			}

			this.isLoading = false;
		}
	}

	@Watch("systemViewData")
	private async fetchChildHealthStats(): Promise<void> {
		if(!this.systemViewData) {
			return;
		}

		var request: SystemViewChildHealthRequest = {
			serverId: this.selectedServer ? this.selectedServer.objectId : null,
			items: this.systemViewData.map(row => {
				return (
					{
						objectId: row.objectId,
						objectTypeId: row.objectTypeId,
					}
				);
			})
		};

		this.pageHealthStats = await api.getSystemViewChildHealthStats(request);
		this.isViewLoading = false;
	}

	@Watch("statusFilter")
	private async onStatusFilterChange(): Promise<void> {
		var statusFilter = this.statusFilter ? this.statusFilter.toLowerCase() : "";
		switch(statusFilter){
			case "healthy":
				this.requestParams.excludeHealthyItems = false;
				this.requestParams.excludeUnhealthyItems = true;
				this.requestParams.excludeUntestedItems = true;
				break;
			case "unhealthy":
				this.requestParams.excludeHealthyItems = true;
				this.requestParams.excludeUnhealthyItems = false;
				this.requestParams.excludeUntestedItems = true;
				break;
			default:
				this.requestParams.excludeHealthyItems = false;
				this.requestParams.excludeUnhealthyItems = false;
				this.requestParams.excludeUntestedItems = false;
		}
		this.getViewData();
	}

	private updateSelectedArea(): void {
		this.selectedGroupId = this.selectedArea[this.selectedArea.length-1].groupID;
	}

	@Emit("itemSelected")
	private async onItemSelected(node: SystemViewTreeNode): Promise<SystemViewTreeNode> {
		return node;
	}

	private loadHome(): void {
		try {
			this.isLoadingAreas = true;
			this.selectedArea = this.rootArea;
			this.updateSelectedArea();
		} catch (ex) {
			console.error("Unexpected error loading SystemView home area filter: " + ex);
		}

		this.isLoadingAreas = false;
	}

	private handleViewLevelClick(level: number): void {
		this.isViewLoading = true;
		if(level === this.ViewLevelsEnum.Appliances) {
			this.healthStatus = "All";
			this.selectedAppliance = null;
			this.selectedServer = null;
			this.selectedSync = null;
		} else if (level === this.ViewLevelsEnum.Servers) {
			this.selectedServer = null;
			this.selectedSync = null;
		} else if (level === this.ViewLevelsEnum.Syncs) {
			this.selectedSync = null;
		}

		this.setViewLevel(level);
	}

	private setViewLevel(level: number): void {
		this.viewLevelSelected = level;
	}

	@Watch("viewLevelSelected")
	private onViewSelectionChanged(): void {
		this.recordsPage = 1;
		this.getViewData();
	}

	private onPageClick(page: number): void {
		this.recordsPage = page || this.recordsPage;
		this.getViewData();
	}

	private getNodeIcon(objectTypeId: number, lastInteractionHealthy): string {
		var css = "fa ";

		switch(objectTypeId) {
			case ObjectTypeId.Server:
				css += "fa-server";
				break;
			case ObjectTypeId.GroupSync:
				css += "fa-sync";
				break;
			case ObjectTypeId.Alarm:
				css += "fa-bell";
				break;
			case ObjectTypeId.Camera:
				css += "fa-cctv";
				break;
			case ObjectTypeId.Door:
				css += "fa-door-open";
				break;
			case ObjectTypeId.Audio:
				css += "fa-volume";
				break;
			case ObjectTypeId.Relay:
				css += "fa-sign-out";
				break;
			default:
				css += "fa-circle";
		}

		if(lastInteractionHealthy === true)
		{
			css += " healthy-item";
		}
		else if(lastInteractionHealthy === false)
		{
			css += " unhealthy-item";
		}
		return css;
	}

	private handleDrillDownClick(object: SystemViewResponse): void {
		this.recordsPage = 1;
		switch(this.viewLevelSelected) {
			case this.ViewLevelsEnum.Appliances:
				this.selectedAppliance = object;
				break;

			case this.ViewLevelsEnum.Servers:
				this.selectedServer = object;
				break;

			case this.ViewLevelsEnum.Syncs:
				this.selectedSync = object;
				break;
		}

		this.setViewLevel(this.viewLevelSelected + 1);
	}

	private handleHealthClickThrough(healthStatus: string, object: SystemViewResponse): void {
		this.recordsPage = 1;
		this.healthStatus = healthStatus;
		this.handleDrillDownClick(object);
	}

	private getRowHealthStats(object: SystemViewResponse): SystemViewChildHealthResponse {
		var emptyResult = {
			objectId: object.objectId,
			objectTypeId: object.objectTypeId,
			all: 0,
			healthy: 0,
			unhealthy: 0
		};

		if(!this.pageHealthStats) {
			return emptyResult;
		}

		var filteredItems = this.pageHealthStats.filter(row => row.objectId === object.objectId && row.objectTypeId === object.objectTypeId);

		if(filteredItems.length !== 1) {
			return {
				objectId: object.objectId,
				objectTypeId: object.objectTypeId,
				all: 0,
				healthy: 0,
				unhealthy: 0
			};
		}

		return filteredItems[0];
	}

	private getUnhealthyRowIndicatorClass(object: SystemViewResponse) : string {
		return this.getRowHealthStats(object).unhealthy > 0 ? "row-indicator-unhealthy" : "row-indicator-unhealthy-zero";
	}

	private unsetSelectedSync(): void {
		this.selectedSync = null;

		this.setViewLevel(this.ViewLevelsEnum.Syncs);
	}

	private unsetSelectedServer(): void {
		this.selectedServer = null;
		this.selectedSync = null;

		this.setViewLevel(this.ViewLevelsEnum.Servers);
	}

	private unsetSelectedAppliance(): void {
		this.selectedAppliance = null;
		this.selectedServer = null;
		this.selectedSync = null;

		this.setViewLevel(this.ViewLevelsEnum.Appliances);
	}
}
